import { useState } from "react";
import { format } from "date-fns";
import { Loader2, X, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import apiClient from "./api";

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

const PRESETS = [
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
  { label: "In 2 weeks", days: 14 },
];

export default function RescheduleReminderModal({ reminder, open, onClose, onSaved, presetReason }) {
  const [date, setDate] = useState();
  const [reason, setReason] = useState(presetReason || "");
  const [submitting, setSubmitting] = useState(false);

  if (!open || !reminder) return null;

  const pickPreset = (days) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    setDate(d);
  };

  const submit = async () => {
    if (!date) {
      toast.error("Pick a new reminder date");
      return;
    }
    setSubmitting(true);
    try {
      const newNotes = [reminder.notes, reason].filter(Boolean).join("\n\n");
      const { data } = await apiClient.patch(`/api/admin/reminders/${reminder.id}`, {
        remind_on: format(date, "yyyy-MM-dd"),
        status: "pending",
        notes: newNotes || undefined,
      });
      toast.success("Reminder rescheduled");
      onSaved && onSaved(data);
      onClose();
    } catch {
      toast.error("Could not reschedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reschedule-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#C9BCFF]">Reschedule</p>
            <h2 className="font-heading text-lg font-bold text-white truncate max-w-[220px]">
              {reminder.customer_name}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="reschedule-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Quick pick</label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => pickPreset(p.days)}
                  data-testid={`reschedule-preset-${p.days}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:border-white/30 transition"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">New reminder date *</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="reschedule-date-picker"
                  className={`${inputCls} text-left flex items-center justify-between`}
                >
                  <span className={date ? "text-white" : "text-white/40"}>
                    {date ? format(date, "PPP") : "Pick a date"}
                  </span>
                  <CalIcon className="w-4 h-4 text-[#C9BCFF]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10 z-[90]" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Why reschedule? (optional)</label>
            <textarea
              data-testid="reschedule-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="No response · Asked to call back later · Customer thinking it over…"
              className={inputCls + " resize-none"}
            />
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="reschedule-submit"
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : "Reschedule reminder"}
          </button>
        </div>
      </div>
    </div>
  );
}
